import { PropsWithChildren, useEffect, useState } from "react";
import {
  FieldArray,
  FieldArrayPath,
  FieldArrayWithId,
  FieldErrors,
  FieldPath,
  FieldValues,
  RegisterOptions,
  UseFieldArrayReturn,
} from "react-hook-form";
import styled from "styled-components";


import { translate } from "../../../intl";
import { PrimaryButton } from "../../../ui";
import { BaseFieldProps } from "../BaseField";
import { FieldsArrayRow } from "./FieldsArrayRow";

const Container = styled.div`
  width: 100%;
  transition: 0.2s ease;

  h5 {
    margin: 0;
    margin-bottom: 8px;
  }
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
`;

export type RenderType<
  TFieldValues extends FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues>
> = (
  prefix: string,
  field: FieldArrayWithId<TFieldValues, TFieldArrayName>,
  index: number
) => JSX.Element | null;

export interface FieldsArrayProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TFieldArrayName extends FieldArrayPath<TFieldValues>
> extends BaseFieldProps<TFieldValues, TName> {
  renderRow: RenderType<TFieldValues, TFieldArrayName>;
  control: UseFieldArrayReturn<TFieldValues, TFieldArrayName>;
  hideDefaultControl?: boolean;
  defaultValue?: Partial<Record<string, any>>[];
  showAddButton?: boolean;
  showAddButtonName?: string;
}

export function FieldsArray<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TFieldArrayName extends FieldArrayPath<TFieldValues>
>({
  name,
  label,
  renderRow,
  hideDefaultControl,
  defaultValue,
  rules,
  form,
  readOnly,
  showAddButton = true,
  showAddButtonName,
  control,
}: PropsWithChildren<FieldsArrayProps<TFieldValues, TName, TFieldArrayName>>) {
  const { fields, append, remove } = control;
  const [initialValues, setInitialValues] = useState(defaultValue);

  const errorsByRow = form.formState?.errors?.[name] as FieldErrors<TFieldValues>[] | undefined;

  useEffect(() => {
    if (!fields.length && initialValues?.length) {
      initialValues.forEach((initialValue) => {
        append(initialValue as FieldArray<TFieldValues, TFieldArrayName>);
        setInitialValues([]);
      });
    }
  }, [initialValues, fields, append]);

  return (
    <Container className="field fields-array">
      {label && <h4>{label}</h4>}
      <Fields>
        {fields.map((field, index) => {
          const rowError =
            errorsByRow?.[index] && typeof errorsByRow[index] === "object"
              ? Object.values(errorsByRow[index] as Record<string, unknown>).find(
                (rowFields) =>
                  rowFields && typeof rowFields === "object" && Object.values(rowFields).length
              )
              : undefined;

          return (
            <FieldsArrayRow
              name={name}
              key={field.id}
              field={field}
              index={index}
              renderRow={renderRow}
              rowError={rules && rowError}
              fieldsLength={fields.length}
              readOnly={readOnly}
              remove={remove}
              hideDefaultControl={hideDefaultControl}
              defaultErrorMessage={getDefaultErrorMessage(rules)}
            />
          );
        })}

        {/* add button */}
        {!readOnly && showAddButton && (
          <PrimaryButton
            type="button"
            onClick={() => append({} as FieldArrayWithId<TFieldValues, TFieldArrayName>)}
          >
            {showAddButtonName ?? translate("formGeneric.addAnother")}
          </PrimaryButton>
        )}
      </Fields>
    </Container>
  );
}

const getDefaultErrorMessage = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>(
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >
) => {
  let defaultErrorMessage =
    rules?.required && typeof rules?.required === "string" ? rules?.required : "";

  defaultErrorMessage =
    !defaultErrorMessage && rules?.required === true
      ? translate("formGeneric.requiredField")
      : defaultErrorMessage;

  return defaultErrorMessage;
};

FieldsArray.defaultProps = {
  defaultValue: [],
  initialIndexAllowedToEdit: 0,
};
